"use client";

import clsx from "clsx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AdSlot } from "@/components/AdSlot/AdSlot.component";
import { TopAdFallback } from "@/components/TopAd/components/TopAdFallback.component";
import { useTopAd } from "@/components/TopAd/top-ad.context";
import { useSiteConfiguration } from "@/contexts/site-configuration/site-configuration.context";
import { useIntersectionObserver } from "@/hooks/browser/observers/use-intersection-observer.hook";
import { withDebounce } from "@/utilities/debounce/with-debounce";
type TopAdProps = {
  readonly className?: string;
};
const debounceDelay = 300;

/**
 * The top banner section is handled in this way:
 *
 * 1) Initially we display or hide this section based on the default settings from the API
 * 2) If at some page we do not receive a banner from GAM we show a fallback banner instead (only if the section is already expanded)
 * 3) If the section is hidden but we at some point receive a banner from GAM - we expand the section for the rest of the user session
 *
 * This way we limit "content jumping" across pages, limiting it to the first page render only.
 */
export function TopAd({
  className
}: TopAdProps) {
  // We need to keep state out in _app.page.tsx, to avoid the state to rerender with each route change
  const {
    isIntersecting: isTopAdInView,
    elementRef: topAdRef
  } = useIntersectionObserver<HTMLDivElement>({
    threshold: 0
  });
  const {
    isHidden: isHiddenInSession,
    setIsHidden: setIsHiddenInSession,
    setIsTopAdInView,
    setTopAdHeight
  } = useTopAd();
  const [isTopEmpty, setIsTopEmpty] = useState<boolean | null>(null);
  const {
    isTopBannerExpanded
  } = useSiteConfiguration();
  const isHiddenInConfig = useRef(!isTopBannerExpanded);
  const {
    links: {
      fallbackBanner
    }
  } = useSiteConfiguration();

  // biome-ignore lint/correctness/useExhaustiveDependencies: We need to listen for if banner is hidden
  useLayoutEffect(() => {
    if (!topAdRef.current) {
      return;
    }
    function updateHeight() {
      const height = topAdRef.current?.getBoundingClientRect().height || 0;
      setTopAdHeight(height);
    }
    updateHeight();
    const debouncedUpdateHeight = withDebounce(updateHeight, debounceDelay);
    window.addEventListener("resize", debouncedUpdateHeight);
    return () => {
      window.removeEventListener("resize", debouncedUpdateHeight);
    };
  }, [topAdRef, isHiddenInConfig, isHiddenInSession]);
  useEffect(() => {
    // If banner has been shown once, don't ever hide again cf. 3).
    // This component does remount from time to time, so we need this check.
    if (isHiddenInSession === false) {
      return;
    }
    setIsHiddenInSession(isHiddenInConfig.current);
  }, [isHiddenInSession, setIsHiddenInSession]);
  useEffect(() => {
    // First mount - we need to wait for isHiddenInSession and isTopEmpty to be initialised
    if (isHiddenInSession === null || isTopEmpty === null) {
      return;
    }

    // If banner has been shown once, don't ever hide again cf. 3)
    if (!isHiddenInSession) {
      return;
    }

    // If the banner is hidden but we at some point receive a banner from GAM - we expand the section for the rest of the user session
    if (!isTopEmpty) {
      setIsHiddenInSession(false);
    }
  }, [isHiddenInSession, isTopEmpty, setIsHiddenInSession]);
  useEffect(() => {
    setIsTopAdInView(isTopAdInView);
  }, [isTopAdInView, setIsTopAdInView]);

  // If isHiddenInConfig is false, we don't ever want to hide it.
  // So only if banner does not have top banner expanded, we can assume that a non-initialsed banner (isHiddenInSession === null)
  // or isHiddenInSession means that it should be hidden.
  const isHidden = isHiddenInConfig.current && (isHiddenInSession === null || isHiddenInSession);
  return <div className={clsx(className, "w-full max-w-full bg-nero", isHidden && "hidden")} ref={topAdRef}>
      <div className="container h-[160px] max-w-[320px] p-0 lg:h-[180px] lg:max-w-[930px]">
        <AdSlot className="h-[160px] w-[320px] lg:h-[180px] lg:w-[930px]" domId="top" onBeforeShow={(isEmpty: boolean) => setIsTopEmpty(isEmpty)} />
        {fallbackBanner !== "" && fallbackBanner !== undefined ? <TopAdFallback className={clsx((isTopEmpty === null || !isTopEmpty) && "hidden")} /> : null}
      </div>
    </div>;
}